import { X, Info, Ruler, AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';

/**
 * Comprehensive size guide for One Maillot.
 * Measurements provided by user — official One Maillot reference.
 */

const TABLES = {
  homme: {
    label: 'Tailles Homme (US/EU)',
    headers: ['Taille', 'Longueur', 'Poitrine', 'Stature', 'Poids (kg)'],
    rows: [
      ['S',   '69–71', '53–55', '162–170', '50–62'],
      ['M',   '71–73', '55–57', '170–176', '62–78'],
      ['L',   '73–75', '57–58', '176–182', '78–83'],
      ['XL',  '75–78', '58–60', '182–190', '83–90'],
      ['XXL', '78–81', '60–62', '190–195', '90–97'],
    ],
  },
  femme: {
    label: 'Tailles Femme (US/EU)',
    headers: ['Taille', 'Longueur', 'Poitrine', 'Stature'],
    rows: [
      ['S',  '63', '80', '155–165'],
      ['M',  '65', '86', '165–170'],
      ['L',  '67', '92', '170–175'],
      ['XL', '69', '96', '175–180'],
    ],
  },
  player: {
    label: 'Tailles Player (coupe ajustée)',
    headers: ['Taille', 'Longueur', 'Poitrine', 'Stature', 'Poids (kg)'],
    rows: [
      ['S',   '67–69', '49–51', '162–170', '50–62'],
      ['M',   '69–71', '51–53', '170–176', '62–78'],
      ['L',   '71–73', '53–55', '176–182', '78–83'],
      ['XL',  '73–75', '55–57', '182–190', '83–90'],
      ['XXL', '75–77', '57–59', '190–195', '90–97'],
    ],
  },
  enfant: {
    label: 'Tailles Enfant',
    headers: ['Taille', 'Taille num.', 'Âge', 'Stature', 'Longueur', '½ Poitrine', 'Longueur short', '½ Tour de taille'],
    rows: [
      ['3XS', '16', '2–3',   '95–105',  '44', '32', '32', '20–37'],
      ['2XS', '18', '3–4',   '105–115', '47', '34', '34', '21–39'],
      ['XS',  '20', '4–5',   '115–125', '50', '36', '36', '22–41'],
      ['S',   '22', '6–7',   '125–135', '53', '38', '38', '23–42'],
      ['M',   '24', '8–9',   '135–145', '56', '40', '39', '24–44'],
      ['L',   '26', '10–11', '145–155', '58', '42', '40', '25–47'],
      ['XL',  '28', '12–13', '155–160', '61', '44', '43', '26–50'],
    ],
  },
  training: {
    label: 'Tailles Training (Homme / Femme)',
    headers: ['Taille', 'Haut', 'Pantalon', 'Poitrine', 'Carrure', 'Stature', 'Poids (kg)'],
    rows: [
      ['S',   '68', '98.5', '100', '75', '155–170', '55–65'],
      ['M',   '70', '101',  '104', '77', '165–175', '60–75'],
      ['L',   '72', '104',  '108', '79', '170–185', '70–85'],
      ['XL',  '74', '106',  '112', '81', '180–195', '80–100'],
      ['XXL', '76', '109',  '116', '83', '195–210', '95–115'],
    ],
  },
  trainingEnfant: {
    label: 'Tailles Enfant Training',
    headers: ['Taille', 'Âge', 'Longueur', 'Poitrine', 'Carrure', 'Pantalon', 'Stature'],
    rows: [
      ['10#', '6–7',   '54', '82', '58.5', '73.5', '110–115'],
      ['12#', '8–9',   '56', '86', '60.5', '76',   '115–125'],
      ['14#', '10–11', '58', '90', '62.5', '78.5', '125–135'],
      ['16#', '12–13', '60', '94', '64.5', '81',   '135–145'],
      ['18#', '14–15', '62', '98', '66.5', '83.5', '145–155'],
    ],
  },
};

const TABLE_ORDER = ['homme', 'femme', 'player', 'enfant', 'training', 'trainingEnfant'];

export default function SizeGuideModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      data-testid="size-guide-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white text-zinc-900 w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[88vh] overflow-y-auto sm:border sm:border-zinc-200"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 flex items-center justify-between px-4 sm:px-6 py-3.5">
          <div className="flex items-center gap-2">
            <Ruler size={18} className="text-zinc-900" />
            <h2 className="font-heading text-base sm:text-lg font-bold uppercase tracking-wider">Guide des tailles</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-zinc-500 hover:text-zinc-900 transition-colors"
            aria-label="Fermer"
            data-testid="size-guide-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-6 py-5 space-y-6 font-body text-sm">
          {/* How to measure */}
          <section>
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
              <Ruler size={14} className="text-zinc-700" /> Comment prendre vos mesures ?
            </h3>
            <ul className="space-y-1.5 text-zinc-700 leading-relaxed list-disc list-outside ml-4">
              <li><strong>Stature :</strong> taille debout, pieds nus, du sol jusqu'à la tête</li>
              <li><strong>Poitrine :</strong> tour de poitrine au point le plus large (sous les aisselles)</li>
              <li><strong>Tour de taille :</strong> au niveau le plus fin de l'abdomen</li>
              <li><strong>Longueur :</strong> de l'épaule jusqu'en bas du vêtement</li>
              <li><strong>Carrure :</strong> largeur d'épaule à épaule (dans le dos)</li>
              <li><strong>Longueur pantalon :</strong> de l'entrejambe à la cheville</li>
            </ul>
          </section>

          {/* Half-sizes note */}
          <section className="bg-zinc-50 border border-zinc-200 px-4 py-3">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
              <Info size={14} className="text-zinc-700" /> À propos des demi-mesures
            </h3>
            <p className="text-zinc-700 leading-relaxed mb-2">
              Les mesures comme <strong>½ poitrine</strong> ou <strong>½ tour de taille</strong> sont prises sur un vêtement posé à plat.
            </p>
            <ul className="space-y-1 text-zinc-700 leading-relaxed">
              <li>👉 Multipliez par <strong>2</strong> pour obtenir le tour complet</li>
              <li>👉 Le résultat est légèrement supérieur pour assurer le confort</li>
            </ul>
          </section>

          {/* Conseils */}
          <section>
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
              <Info size={14} className="text-zinc-700" /> Conseils
            </h3>
            <ul className="space-y-1.5 text-zinc-700 leading-relaxed list-disc list-outside ml-4">
              <li>Utilisez un mètre ruban souple</li>
              <li>Mesurez directement sur le corps</li>
              <li><strong>Entre deux tailles → prenez la plus grande</strong></li>
              <li>Pour les enfants → privilégiez la <strong>stature</strong> plutôt que l'âge</li>
            </ul>
            <div className="mt-3 bg-amber-50 border-l-2 border-amber-500 px-3 py-2 flex gap-2 text-xs">
              <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-amber-900">
                Une variation de <strong>1 à 3 cm</strong> est possible (mesure manuelle).
              </p>
            </div>
          </section>

          {/* Tables */}
          {TABLE_ORDER.map((key) => {
            const t = TABLES[key];
            return (
              <section key={key}>
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-2">{t.label}</h3>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="w-full text-xs sm:text-sm border-collapse min-w-[460px]">
                    <thead>
                      <tr className="bg-zinc-900 text-white">
                        {t.headers.map((h, i) => (
                          <th key={i} className="text-left py-2 px-3 font-bold uppercase tracking-wider text-[10px] sm:text-xs whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {t.rows.map((row, ri) => (
                        <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}>
                          {row.map((cell, ci) => (
                            <td key={ci} className="py-2 px-3 border-b border-zinc-100 whitespace-nowrap">
                              {ci === 0 ? <strong>{cell}</strong> : cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}

          {/* Footer reminder */}
          <p className="text-xs text-zinc-500 italic text-center pt-2 border-t border-zinc-200">
            En cas de doute, n'hésitez pas à nous contacter — nous vous aidons à choisir la bonne taille.
          </p>
        </div>
      </div>
    </div>
  );
}
